// Scene 2 — Set the first price. Miko asks, then three price choices, each
// previewing expected demand/revenue/profit. Choosing dispatches SetPrice.

import { businessWeeklyResult, findBusiness, priceFactor, repFactor } from '@biz-kid/engine';
import { builderContent } from '@biz-kid/engine/content/builder';
import { useDispatch, useGameState } from '@biz-kid/store';
import { Bar, Card, Eyebrow, money } from '../../ui/kit.js';
import { StoryShell, StoryChoice, PreviewRows, choiceGrid } from '../storyKit.js';

export type PriceChoiceProps = { onComplete: () => void };

type PricePick = { label: string; price: number; tone: 'warm' | 'fair' | 'premium' };

const PICKS: PricePick[] = [
  { label: 'B$5', price: 5, tone: 'warm' },
  { label: 'B$8', price: 8, tone: 'fair' },
  { label: 'B$12', price: 12, tone: 'premium' },
];

const SUBTITLES: Record<PricePick['tone'], string> = {
  warm: 'Cheap. A lot of people will buy.',
  fair: 'Right around the going rate.',
  premium: 'Fancy. Only the keen ones bite.',
};

export function PriceChoice({ onComplete }: PriceChoiceProps) {
  const dispatch = useDispatch();
  const businesses = useGameState((s) => s.businesses);
  const reputation = useGameState((s) => s.profile.reputation);

  const business = businesses[0];
  const def = findBusiness(builderContent, 'smoothieStand')!;

  const choose = (price: number) => {
    if (!business) {
      console.warn('No business to set price on — skipping');
      onComplete();
      return;
    }
    const err = dispatch({ kind: 'SetPrice', businessId: business.id, price });
    if (err) console.warn('SetPrice failed:', err);
    onComplete();
  };

  return (
    <StoryShell eyebrow="Day 1 · Set your price">
      <>
        <Card>
          <Eyebrow color="var(--brand)">What will you charge?</Eyebrow>
          <p style={{ margin: '6px 0 0', fontSize: 15, color: 'var(--text)', lineHeight: 1.5 }}>
            Each drop costs <b>B$3</b> to make. Pick a price and see what happens.
          </p>
        </Card>
        <div style={choiceGrid}>
          {PICKS.map((p) => {
            const preview = businessWeeklyResult({
              baseDemand: def.baseDemand,
              price: p.price,
              sweetPrice: def.sweetPrice,
              marketingSpend: 0,
              qualityFactor: def.qualityFactorDefault,
              repFactor: repFactor(reputation),
              capacity: def.capacity,
              cogsPerUnit: def.cogsPerUnit,
              fixedPerWeek: def.fixedPerWeek,
            });
            const pf = priceFactor(p.price, def.sweetPrice);
            const profitAfterLiving = preview.activeIncome - 40;
            return (
              <StoryChoice
                key={p.price}
                tone={p.tone}
                label={p.label}
                subtitle={SUBTITLES[p.tone]}
                onClick={() => choose(p.price)}
                preview={
                  <div style={{ display: 'grid', gap: 8 }}>
                    <PreviewRows
                      rows={[
                        ['Sold this week', `${preview.unitsSold}`],
                        ['Revenue', money(preview.revenue)],
                        [
                          'Profit after living costs',
                          <span style={{ color: profitAfterLiving >= 0 ? 'var(--up)' : 'var(--down)' }}>
                            {money(profitAfterLiving, { sign: true })}
                          </span>,
                        ],
                      ]}
                    />
                    <Bar frac={Math.min(1, pf * 0.7)} color="var(--brand)" />
                  </div>
                }
              />
            );
          })}
        </div>
      </>
    </StoryShell>
  );
}
